/**
 * 【執筆の見本】
 *
 * 国データと史実メモの品質基準を示すための参照実装。
 * 他の国を書くときは、この2件と同じ密度・同じ書きぶりに揃えること。
 *
 * 守るべきこと（docs/05-学習設計.md §4）:
 *   1. ゲームの都合で史実を改変した箇所は gameNote に必ず書く
 *   2. 通説と異説がある場合は caveat に必ず書く
 *   3. 武将を「英雄」「悪」と評価しない。戦闘行為を美化しない
 *   4. 合戦だけでなく、検地・楽市楽座・治水など生活面の事実も入れる
 *   5. detail は 1/2/3 の3段階すべて必須。学年に応じて語彙と長さを変える
 */

import type { Province } from '../types';

/** 平野の国の見本。地の利は騎馬になる。 */
export const OWARI: Province = {
  id: 4,
  kind: 'province',
  name: '尾張',
  reading: 'おわり',
  region: 'tokai',
  kokudaka: 3,
  defense: 2,
  terrain: 'plain',
  historicalKokudaka: 57,
  note: {
    summary:
      '濃尾平野の南部を占める豊かな国。津島・熱田の湊で商業が栄え、織田氏の本拠となった。',
    detail: {
      1: 'いまの愛知県の西がわにあった国です。ひろい平野で米がたくさんとれ、津島や熱田という港もあったので、とても豊かな国でした。この国で力をつけた織田信長が、桶狭間の戦いで今川義元をやぶり、天下統一をめざしました。',
      2: '現在の愛知県西部にあたる国。濃尾平野の南部を占め、木曽川の水運と津島・熱田という二つの湊を持っていたため、農業と商業の両方で富んだ。室町幕府の守護は斯波氏だったが、守護代の織田氏が実権を握り、その織田氏自身も清洲と岩倉に分かれて争った。信長はその一族の弾正忠家に生まれ、家格の低い立場から国内を統一している。1560年の桶狭間の戦いで駿河の今川義元を破ったことが、信長が畿内へ進出する転機となった。慶長3年（1598年）の集計では約57万石を数える大国である。',
      3: '現在の愛知県西部。濃尾平野南部の沖積地を占め、木曽川水系の水運と、津島・熱田という二つの湊を擁したことで、石高以上の経済力を持った。室町幕府の守護は斯波氏だが、応仁の乱以降に守護代の織田氏が台頭し、織田氏自体も清洲方（織田大和守家）と岩倉方（織田伊勢守家）に分裂して抗争した。信長はさらにその配下の弾正忠家の出身であり、家格としては低い立場から国内統一を果たしている。低平な沖積地は水害を受けやすく、木曽川の流路を固定する御囲堤の築造は1609年（慶長14年）を待つことになる。石高は慶長3年（1598年）の国別集計に約57万石とあり、いわゆる太閤検地の成果を集約したものとして扱われるが、検地の年次や竿・枡の基準は国ごとに異なり、全国一律の調査による数値ではない。津島・熱田からの流通収入が織田氏の軍事力を支えたとする見方は有力だが、その規模を直接示す同時代史料は乏しく、「兵農分離」を織田氏の特質とする理解とあわせて、近年の研究では見直しが進んでいる。',
    },
    year: [1467, 1600],
    keywords: ['尾張', '織田氏', '濃尾平野', '津島', '熱田', '桶狭間'],
    caveat:
      '織田氏の強さを津島・熱田の流通収入に求める説明は広く知られていますが、両湊からの収入の規模を直接示す同時代史料は乏しく、推定に頼る部分が大きく残ります。また兵と農を切り離した常備軍を織田氏の特質とする「兵農分離」の理解も、近年の研究では他の大名との違いは程度の差にすぎないとする見直しが進んでいます。',
    gameNote:
      '盤上の石高ランクは遊びやすさのために調整した相対値です。実際の尾張は太閤検地で約57万石でした。',
  },
};

/** 山国の見本。地の利は弓になる。武田氏の本拠。 */
export const KAI: Province = {
  id: 21,
  kind: 'province',
  name: '甲斐',
  reading: 'かい',
  region: 'tokai',
  kokudaka: 2,
  defense: 4,
  terrain: 'mountain',
  historicalKokudaka: 23,
  yields: [{ res: 'zeni', n: 1 }],
  note: {
    summary:
      '四方を山に囲まれた国。米はとれにくいが金山を持ち、治水と鉱山開発で国力を蓄えた。武田氏の本拠。',
    detail: {
      1: 'いまの山梨県にあった国です。まわりを高い山にかこまれているので、せめこむのがむずかしい国でした。米はあまりとれませんが、金がとれました。武田信玄が川のはんらんをふせぐ工事をしたことでも知られています。',
      2: '現在の山梨県にあたる国。四方を山地に囲まれ、平地が少ないため石高は高くない。しかし黒川金山などの金山を持ち、金は軍資金や恩賞に用いられた。武田信虎・信玄の二代で国内が統一され、信玄は釜無川の氾濫を防ぐための堤（信玄堤と呼ばれる）を築くなど治水にも力を入れた。領国を治めるため「甲州法度之次第」という分国法も定められている。山に囲まれた地形は防御に適する一方、外へ出るには峠を越える必要があり、信濃や駿河への進出が長く課題となった。',
      3: '現在の山梨県。甲府盆地を中心とする内陸国で、四囲を赤石・関東・奥秩父の各山地に囲まれる。可耕地が限られるため石高は伸びず、慶長3年（1598年）の国別集計では約23万石にとどまる。この数値もいわゆる太閤検地の成果を集約したものだが、検地の年次や基準は国ごとに異なり、金や山の産物による収入を反映した数字ではない。黒川金山をはじめとする金山の産出が財政を補ったとされる。守護武田氏は国人層の自立性が強く長く国内統一に手間取ったが、武田信虎期に一応の統一をみる。信玄期には「甲州法度之次第」が定められ、家臣間の争いを当主の裁定に委ねる原則が示された。治水事業は釜無川・御勅使川の合流点を対象としたもので、「信玄堤」の名で知られる。ただし現在残る堤防の大半は江戸期以降の改修によるもので、信玄当時の姿をそのまま伝えるものではない。金山の産出量についても同時代史料が乏しく後世の記録に依拠する部分が大きいため、金の富が武田氏の軍事力を支えたとする説明をどこまで裏づけられるかは、研究上なお議論がある。',
    },
    year: [1467, 1582],
    keywords: ['甲斐', '武田氏', '甲府盆地', '金山', '信玄堤', '治水'],
    caveat:
      '「信玄堤」として現存する堤防の多くは江戸時代以降の改修によるもので、信玄当時の構造をそのまま残しているわけではありません。金山の産出量も後世の記録に頼る部分が大きく、確かな数字はわかっていません。',
    gameNote:
      '盤上では山国の防御を高く設定していますが、これは地形の効果を遊びに落とし込んだものです。実際の甲斐が「攻め落とせない国」だったわけではなく、1582年に織田・徳川連合軍の侵攻で武田氏は滅びました。',
  },
};
